import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    ViewEncapsulation,
} from "@angular/core";
import { attendanceDisplayName, attendanceState, AttendanceState, attendanceStateName } from "./attendance.service";
import { s25Resource } from "../../../util/s25.resource";
import { ContactService } from "../../../services/contact.service";
import { FormsModule } from "@angular/forms";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeAttendanceStore } from "./se.attendance.store";

type Action = { value: AttendanceState; label: string };

@Component({
    standalone: true,
    selector: "s25-ng-attendance-state",
    template: `
        @if (saving()) {
            <s25-ng-loading-inline-static [text]="'Saving...'" />
        } @else if (!readonly() && options()) {
            <select [ngModel]="state()" (ngModelChange)="onStateChange($event)" [attr.aria-label]="accessibleName()">
                <option [ngValue]="state()">{{ stateLabel() }}</option>
                @for (option of options(); track option) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                }
            </select>
        } @else {
            {{ stateLabel() }}
        }
    `,
    styles: `
        :host {
            background: var(--color--text-muted);
            color: white;
            padding: 0.3em 0.5em;
            border-radius: 6px;
            font-size: 0.8em;

            &[data-state="INTERESTED"] {
                background: var(--cn-blue);
            }

            &[data-state="REGISTERED"] {
                background: var(--color--registration);
            }

            &[data-state="WAIT_LIST"] {
            }

            &[data-state="CHECKED_IN"] {
                background: var(--color--registration);
            }

            &[data-state="NO_SHOW"] {
            }

            &[data-state="CANCELED"] {
            }

            &[data-state="CHECKED_OUT"] {
                background: var(--color--registration);
            }
        }

        :host ::ng-deep s25-ng-loading-inline-static svg.c-loading {
            width: 1em;
        }

        :host ::ng-deep s25-ng-loading-inline-static svg.c-loading > circle {
            stroke: currentColor;
        }

        select {
            border: none;
            background: transparent;
            color: inherit;
            width: 14ch;
            cursor: pointer;

            option {
                color: var(--color--text);
                background: var(--color--surface-bg);
            }
        }
    `,
    host: {
        "[attr.data-state]": "stateName()",
    },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25LoadingInlineModule],
})
export class SeAttendanceStateComponent {
    readonly state = input.required<AttendanceState>();
    readonly contactIdInput = input<number>(undefined, { alias: "contactId" });
    readonly occurrenceId = input.required<number>();
    readonly readonly = input.required<boolean>();
    readonly contactName = input<string>();
    readonly occurrenceLabel = input<string>();

    currentContactId = s25Resource({ loader: () => ContactService.getCurrentId() });

    readonly attendanceStore = inject(SeAttendanceStore);
    readonly orgSecStore = inject(SeOrgSecurityStore);

    readonly saving = linkedSignal(() => {
        // Reset to false on state change
        this.state();
        return false;
    });
    readonly contactId = computed(() => this.contactIdInput() ?? this.currentContactId.value());
    readonly stateLabel = computed(() => {
        return attendanceDisplayName[this.state()];
    });
    readonly stateName = computed(() => {
        return attendanceStateName[this.state()];
    });
    readonly accessibleName = computed(() => {
        const name = this.contactName();
        const occurrence = this.occurrenceLabel();
        const base = name ? `${name} attendance status` : "Attendance status";
        return occurrence ? `${base}, ${occurrence}` : base;
    });
    readonly options = computed((): Action[] | undefined => {
        const isCurrentContact = this.contactId() === this.currentContactId.value();
        const canManageAttendees = this.orgSecStore.canManageAttendees();
        if (!(canManageAttendees || isCurrentContact)) return undefined;

        const actions: { value: AttendanceState; label: string }[] = [];
        if (isCurrentContact) actions.push({ value: attendanceState.NOT_INTERESTED, label: "Unfollow" });

        if (canManageAttendees) {
            actions.push(...(SeAttendanceStateComponent.managerActions[this.state()] ?? []));
        }
        return actions;
    });

    private static readonly managerActions: Partial<Record<AttendanceState, Action[]>> = {
        [attendanceState.INTERESTED]: [
            { value: attendanceState.NOT_INTERESTED, label: "Remove" },
            { value: attendanceState.REGISTERED, label: "Register" },
        ],
        [attendanceState.REGISTERED]: [
            { value: attendanceState.INTERESTED, label: "Unregister" },
            { value: attendanceState.CHECKED_IN, label: "Check In" },
            { value: attendanceState.NO_SHOW, label: "No Show" },
        ],
        [attendanceState.CHECKED_IN]: [
            { value: attendanceState.REGISTERED, label: "Undo" },
            { value: attendanceState.CHECKED_OUT, label: "Check Out" },
        ],
        [attendanceState.CHECKED_OUT]: [{ value: attendanceState.CHECKED_IN, label: "Undo" }],
        [attendanceState.NO_SHOW]: [{ value: attendanceState.REGISTERED, label: "Undo" }],
    };

    async onStateChange(action: AttendanceState) {
        this.saving.set(true);
        const ok = await this.attendanceStore.setAttendance(attendanceStateName[action], {
            occurrences: [this.occurrenceId()],
            contactId: this.contactId(),
        });
        if (ok) {
            // If the change was successful, then attendance will be re-loaded, and the "saving" linked signal
            // will be updated when reload is completed
        } else {
            this.saving.set(false);
        }
    }
}
